import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useClerk, useSignIn, useUser } from '@clerk/clerk-react'
import { OneSignal } from '@capacitor/one-signal'
import * as Sentry from '@sentry/react'
import { Outlet, useNavigation, useLocation } from 'react-router-dom'
import { EvervaultProvider } from '@evervault/react'
import { useIsFetching } from '@tanstack/react-query'

import { App } from '@capacitor/app'
import { LiveUpdate } from '@capawesome/capacitor-live-update'
import { Preferences } from '@capacitor/preferences'
import { SplashScreen } from '@capacitor/splash-screen'

// COMPONENTS
import Spinner from '@components/atoms/spinner'

// ROUTES
import ErrorBoundary from './_error'
import AuthRoute from './auth'
import AccountRoute from './account'
import GroupRoute from './group'
// import ExplainerRoute from './explainer'
import SignUpRoute from './sign-up'
import NoticeRoute from './notice'
import WalletExplainerRoute from './wallet-explainer'
import ReferralRoute from './referral/[referralId]'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { AuthAPI, VirtualCardAPI } from '@apis'
import { useGlobalReducer, useGlobalSettings } from './_globalStore/_hooks'
import { GlobalContext } from './_globalStore/reducer'
import { useIoTClient } from '../_mqtt/useIoTClient'
import { useLaunchUrlHandler } from '@hooks/useLaunchUrlHandler'
import { useKeyboardManager } from '@hooks/useKeyboardManager'
import { platform } from '@utils/capacitor-helper'
import { clearLastLaunchUrl } from '@utils/launch-url-storage'
import { isNonEmptyString } from '@utils/type-checkers'
import { useDialogObserver } from '@hooks/useDialogObserver'


export type RootScreen = typeof rootScreens[number]
export const rootScreens = [
    "auth",
    "kyc",
    "account",
    "group",
    "explainer"
] as const

const zeroDeletionRequestTimestamp = '0001-01-01T00:00:00Z'
const hasPendingDeletionRequest = (value: unknown): value is string => (
    isNonEmptyString(value)
    && value !== zeroDeletionRequestTimestamp
)

const Root: FC = () => {
    const { isLoaded, isSignedIn, user } = useUser()
    const location = useLocation()
    const navigation = useNavigation()
    const { signOut } = useClerk()
    const { setActive, signIn } = useSignIn()

    const { setIsServicable, setIsUpdateAvailable, state } = useGlobalSettings()
    const { isUpdateAvailable } = state

    const [temp, setTemp] = useState(true)
    const [isSessionSyncing, setIsSessionSyncing] = useState(false)
    const hasHiddenSplashRef = useRef(false)
    const hasCancelledDeletionRequestRef = useRef<string | null>(null)

    // Initialize global keyboard manager (sets CSS variables for keyboard height)
    useKeyboardManager({
        disabledRoutes: [
            '/group/*/invite',
            '/sign-up/billing'
        ]
    })

    // This hook handles if the app is opened when a link or a push notifiation
    // that contains a link
    useLaunchUrlHandler()

    // Track fetching state for payment methods and groups
    const isFetchingPaymentMethods = useIsFetching({ queryKey: ['payment-methods'] })
    const isFetchingGroups = useIsFetching({
        queryKey: ['user-groups'],
        predicate: (query) => query.state.data === undefined || query.isStale()
    })

    useEffect(() => {
        // If there is a new version available we don't need to perform this check again
        if (isUpdateAvailable.isAvailable || platform === 'web') return

        const listener = App.addListener('resume', async () => {
            try {
                // Check beta preference to ensure we're on the right channel
                const { value: betaValue } = await Preferences.get({ key: 'beta' })
                const isBeta = betaValue === 'true'

                await LiveUpdate.setChannel({
                    channel: import.meta.env.DEV ? 'development' : isBeta ? 'staging' : 'production'
                })
                console.log('[ LiveUpdate ] [ resume ] setChannel()', { channel: import.meta.env.DEV ? 'development' : isBeta ? 'staging' : 'production', isBeta })

                const [currentBundle, latestBundle] = await Promise.all([
                    LiveUpdate.getCurrentBundle(),
                    LiveUpdate.fetchLatestBundle()
                ])
                console.log('[ LiveUpdate ] [ resume ] getCurrentBundle()', currentBundle)
                console.log('[ LiveUpdate ] [ resume ] fetchLatestBundle()', latestBundle)

                // If the latest bundle id is null or if the current bundle id is the same as the
                // latest bundle id then we don't need to go forward.
                if (latestBundle.bundleId === null || currentBundle.bundleId === latestBundle.bundleId) return

                const { bundleId, downloadUrl } = latestBundle
                if (downloadUrl) {
                    console.log('[ LiveUpdate ] [ resume ] download, set')
                    try {
                        await LiveUpdate.downloadBundle({ ...latestBundle, bundleId, url: downloadUrl })
                    } catch (downloadError: any) {
                        if (downloadError?.message !== 'bundle already exists.') {
                            throw downloadError
                        }
                        console.log('[ LiveUpdate ] [ resume ] bundle already downloaded, proceeding to set')
                    }
                    await LiveUpdate.setNextBundle({ bundleId })

                    let isCritical = false
                    if (latestBundle.customProperties && 'critical' in latestBundle.customProperties) {
                        isCritical = latestBundle.customProperties['critical'] === 'true'
                    }

                    setIsUpdateAvailable(true, isCritical)
                }
            } catch (error) {
                console.error('[ LiveUpdate ] [ resume ] issue performing live update', error)
            }
        })

        return () => {
            listener
                .then(handler => handler.remove())
                .catch(error => console.error('[ App.addListener ] resume', error))
        }
    }, [isUpdateAvailable])

    // This checks if the user has permissions already for push notifications. If so
    // we log the user into the OneSignal client so we can use their user id to send
    // push notifications.
    useEffect(() => {
        if (platform === 'web' || !isLoaded || !isSignedIn || !user) return

        async function init() {
            const options: Parameters<typeof OneSignal.login>[0] = { userId: user?.id || '' }

            const { state } = await OneSignal.checkPermissions()
            if (state === 'granted') await OneSignal.login(options)

            if (state === 'prompt' && user) {
                const pushSettings = user.publicMetadata.settings?.notifications?.filter(notif => notif.pushEnabled) || []

                const isNewUser = user.publicMetadata.newUser || true
                if (isNewUser) return

                const { granted } = await OneSignal.requestPermissions()
                if (!granted) return

                await OneSignal.login(options)

                // Update the user's notifications to disable sms and enable push
                if (pushSettings.length === 0) {
                    await VirtualCardAPI.UpdateNotifSettings.fetch(user.id, {
                        notifications: user.publicMetadata.settings && user.publicMetadata.settings.notifications
                            ? user.publicMetadata.settings.notifications.map(notif => {
                                return { ...notif, enabled: false, smsEnabled: false, pushEnabled: true }
                            })
                            : []
                    })
                }
            }
        }

        init()
    }, [platform, isLoaded, isSignedIn, user])

    // Temporary to get around an infinite loop.
    useEffect(() => {
        setIsServicable(temp)
    }, [temp])

    useLayoutEffect(() => {
        if (isLoaded && isSignedIn) {
            if (user) {
                Sentry.setUser({ id: user.id, email: user.primaryEmailAddress?.emailAddress })
                updateNotifSettings(user.id)
            }

            validateSessionId(true)
        }
        if (isLoaded && !isSignedIn) {
            Sentry.setUser(null)
            validateSessionId(false)
        }
    }, [isLoaded, isSignedIn, user])

    useEffect(() => {
        if (!isLoaded || !isSignedIn || !user?.id) return

        const deletionRequested = user.publicMetadata?.deletionRequested
        if (!hasPendingDeletionRequest(deletionRequested) || hasCancelledDeletionRequestRef.current === user.id) return

        hasCancelledDeletionRequestRef.current = user.id

        VirtualCardAPI.UpdateDeletionRequest.fetch(user.id)
            .then(() => {
                user.publicMetadata = {
                    ...user.publicMetadata,
                    deletionRequested: undefined
                }
            })
            .catch((error) => {
                hasCancelledDeletionRequestRef.current = null
                console.error('Failed canceling deletion request', error)
            })
    }, [isLoaded, isSignedIn, user])

    useEffect(() => {
        if (platform === 'web' || hasHiddenSplashRef.current) return
        if (!isLoaded || isSessionSyncing) return

        const path = location.pathname
        const isAuthPath = (
            path === '/' ||
            path === '/auth' ||
            path === '/auth/' ||
            path.startsWith('/sign-in') ||
            path.startsWith('/verify')
        )

        const shouldHide = isSignedIn || !isAuthPath
        if (!shouldHide) return

        hasHiddenSplashRef.current = true
        SplashScreen.hide().catch((error) => console.error('[ SplashScreen.hide ]', error))
    }, [isLoaded, isSignedIn, isSessionSyncing, location.pathname])

    useEffect(() => {
        if (platform === 'web' || hasHiddenSplashRef.current) return

        const timeoutId = window.setTimeout(() => {
            if (hasHiddenSplashRef.current) return
            hasHiddenSplashRef.current = true
            SplashScreen.hide().catch((error) => console.error('[ SplashScreen.hide ] timeout', error))
        }, 2500)

        return () => {
            window.clearTimeout(timeoutId)
        }
    }, [])

    // Due to the way Safari renders content, some screens will retain their height and scroll position from the previous screen.
    // This handles that by automatically scrolling the window up on every navigation.
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        document.body.scrollTop = document.documentElement.scrollTop = 0;
    }, [location.pathname])

    const isLoading = (navigation.state === 'loading' && !!navigation.location) || (navigation.state === 'submitting') || (isFetchingPaymentMethods > 0) || (isFetchingGroups > 0)
    const ref = useRef<HTMLDialogElement>(null)
    useEffect(() => {
        if (ref.current) {
            if (isLoading) ref.current.showModal()
            else ref.current.close()
        }
    }, [isLoading])

    async function getSessionId() {
        await AuthAPI.GetSessionRequest.fetch({})
    }

    async function updateNotifSettings(userId: string) {
        VirtualCardAPI.UpdateNotifSettings.fetch(userId, { notifications: [] })
            .catch(error => console.error("Failed updating notif settings", error))
    }

    async function validateSessionId(signedIn: boolean) {
        setIsSessionSyncing(true)
        try {
            const { response, data } = await AuthAPI.ValidateSessionRequest.fetch({})

            if (response.status === 200 && data.status === 'successful' && !signedIn) {
                const attempt = await signIn?.create({
                    strategy: 'ticket',
                    ticket: data.token
                })

                if (setActive == null || attempt == undefined) return
                else if (attempt.status === 'complete') {
                    await setActive({ session: attempt.createdSessionId })
                }
                return
            } else if (response.status === 400 || response.status === 404) {
                getSessionId()
                return
            } else if (response.status === 401) {
                revokeSessionId()
                return
            } else if (response.status === 403 && !signedIn) {
                rehydrateSessionId()
                return
            }
        } catch (error) {
            console.error('[ validateSessionId ]', error)
        } finally {
            setIsSessionSyncing(false)
        }
    }

    async function rehydrateSessionId() {
        const data = await AuthAPI.RehydrateSessionRequest.fetch({})

        const attempt = await signIn?.create({
            strategy: 'ticket',
            ticket: data.token
        })

        if (setActive == null || attempt == undefined) return
        else if (attempt.status === 'complete') {
            await setActive({ session: attempt.createdSessionId })
        }
    }

    async function revokeSessionId() {
        await AuthAPI.RevokeSessionRequest.fetch({})

        const redirect = `/sign-in`
        await clearLastLaunchUrl().catch((error) => console.error('[ clearLastLaunchUrl ]', error))
        await signOut({ redirectUrl: redirect }).catch((err) => console.log(err))

        if (platform !== 'web') await OneSignal.logout()
    }

    useIoTClient("/user/*/", (user2) => {
        console.log("User Event:", user2)
        if (user) user.reload()
    }, { wildcard: user?.id })

    useIoTClient("/system_event/", ({ service, isReachable }) => {
        console.info("System Event:", { [service]: isReachable })
        setTemp(isReachable)
    })

    return (<>
        {(isLoading) && (
            <dialog ref={ref}>
                <div>
                    <div className="flex flex-col items-center justify-center" style={{ background: 'var(--splitcard-white)', gap: '1rem', padding: '1rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                        <Spinner />
                    </div>
                </div>
            </dialog>
        )}
        <Outlet />
    </>)
}

const Wrapper: FC = () => {
    const globalReducer = useGlobalReducer()
    useDialogObserver()

    return (
        <GlobalContext.Provider value={globalReducer}>
            <EvervaultProvider teamId={import.meta.env.VITE_EVERVAULT_TEAM_ID} appId={import.meta.env.VITE_EVERVAULT_APP_ID}>
                <Root />
            </EvervaultProvider>
        </GlobalContext.Provider>
    )
}

const RootRoute: RouteObject = {
    element: <Wrapper />,
    errorElement: <ErrorBoundary />,
    children: [
        AuthRoute,
        AccountRoute,
        GroupRoute,
        // ExplainerRoute,
        SignUpRoute,
        NoticeRoute,
        WalletExplainerRoute,
        ReferralRoute
    ]
}

export default RootRoute
